import type { StartupError, StartupResource, StartupRoute, StartupTelemetry, StartupWorker } from "@household/shared/startup-telemetry";

type EarlyCapture = { entries: PerformanceResourceTiming[]; dropped: number; supported: boolean; failed: boolean; stop: () => void };
declare global { interface Window { __homeEmStartup?: EarlyCapture } }
type Stage = keyof StartupTelemetry["stages"];
const MAX_MS = 300_000;
const MAX_RESOURCES = 24;
const ENDPOINT = "/api/v1/system/startup-telemetry";
const ms = (value: number) => Math.round(Math.max(0, Math.min(MAX_MS, Number.isFinite(value) ? value : 0)));

/** Resource names never leave the browser; timing fields unavailable to this browser stay absent. */
export function startupResource(entry: PerformanceResourceTiming, origin: string): StartupResource | null {
  let url: URL;
  try { url = new URL(entry.name); } catch { return null; }
  if (url.origin !== origin) return null;
  const category = url.pathname === "/api/v1/auth/status" ? "auth"
    : /^\/assets\/locale-(en|es)-/.test(url.pathname) ? "locale"
      : url.pathname.startsWith("/assets/") && url.pathname.endsWith(".js") ? "script"
        : url.pathname.startsWith("/assets/") && url.pathname.endsWith(".css") ? "style" : null;
  if (!category) return null;
  const result: StartupResource = { category, startMs: ms(entry.startTime), durationMs: ms(entry.duration) };
  if (entry.requestStart > 0 && entry.responseStart >= entry.requestStart) result.waitMs = ms(entry.responseStart - entry.requestStart);
  if (entry.responseStart > 0 && entry.responseEnd >= entry.responseStart) result.downloadMs = ms(entry.responseEnd - entry.responseStart);
  if (Number.isFinite(entry.transferSize)) result.transferBytes = Math.min(100_000_000, Math.max(0, Math.round(entry.transferSize)));
  if (Number.isFinite(entry.decodedBodySize)) result.decodedBytes = Math.min(100_000_000, Math.max(0, Math.round(entry.decodedBodySize)));
  if (entry.workerStart > 0 && entry.fetchStart >= entry.workerStart) result.workerMs = ms(entry.fetchStart - entry.workerStart);
  return result;
}

let report: StartupTelemetry | undefined;
let terminal = false;
let upload: "pending" | "sending" | "sent" | "failed" | "unavailable" = "pending";
let deadline: ReturnType<typeof setTimeout> | undefined;
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let workerReady = false;
let initialized = false;
let warmTimer: ReturnType<typeof setTimeout> | undefined;

export function initializeStartupTelemetry(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  navigator.serviceWorker?.addEventListener("controllerchange", sendWorkerReady);
  // Never invent a durable device/user ID. Older/insecure browsers keep local diagnostics only.
  const loadId = globalThis.crypto?.randomUUID?.();
  if (!loadId) return;
  report = {
    schemaVersion: 1, loadId,
    version: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown",
    build: typeof __APP_BUILD__ === "string" ? __APP_BUILD__ : "unknown",
    locale: "unknown", route: "unknown", outcome: "incomplete", durationMs: 0,
    stages: { entryMs: ms(performance.now()) }, errors: [], resources: [],
    visibility: document.visibilityState === "hidden" ? "hidden" : "visible",
    resourceTimingSupported: Boolean(window.__homeEmStartup?.supported), resourceEntriesDropped: 0
  };
  deadline = setTimeout(() => { startupError("startup_timeout"); finishStartup("incomplete"); }, 30_000);
  window.addEventListener("pagehide", onPageHide, { once: true });
}

export function startupStage(stage: Stage): void {
  if (report && !terminal && report.stages[stage] === undefined) report.stages[stage] = ms(performance.now());
}
export function startupContext(route: StartupRoute, locale: "en" | "es"): void {
  if (report && !terminal) { report.route = route; report.locale = locale; }
}
export function startupError(error: StartupError): void {
  if (report && !terminal && !report.errors.includes(error) && report.errors.length < 16) report.errors.push(error);
}
export function startupHeaders(): Record<string, string> {
  return report && !terminal ? { "X-Home-EM-Load-Id": report.loadId } : {};
}
export function startupAuthResponse(response: Response): void {
  const id = response.headers.get("x-request-id");
  if (report && !terminal && id && /^[A-Za-z0-9._:-]{1,64}$/.test(id)) report.requestId = id;
}

function sendWorkerReady(): void {
  if (!workerReady) return;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  navigator.serviceWorker?.controller?.postMessage({ type: "STARTUP_READY", connection: {
    saveData: connection?.saveData === true, effectiveType: connection?.effectiveType ?? "unknown"
  } });
}

export function startupRouteReady(): void {
  // Offline caching must still work when telemetry is unavailable or already
  // finalized after a failed/slow boot. It never depends on a diagnostic ID.
  if (!workerReady && !warmTimer) warmTimer = setTimeout(() => {
    workerReady = true;
    sendWorkerReady();
  }, 1_000);
  if (!report || terminal) return;
  startupStage("routeReadyMs");
  // Give rendering priority over background cache work and the single upload.
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    finishStartup(report!.errors.length ? "error" : (report!.stages.routeReadyMs ?? 0) >= 5_000 ? "slow" : "ready");
  }, 1_000);
}

function collectResources(): void {
  if (!report) return;
  const early = window.__homeEmStartup;
  early?.stop();
  const entries = early?.entries ?? performance.getEntriesByType("resource") as PerformanceResourceTiming[];
  const mapped = entries.map((entry) => startupResource(entry, window.location.origin)).filter((entry): entry is StartupResource => entry !== null);
  // Reserve space for auth/locales even when unrelated scripts dominate the startup burst.
  mapped.sort((a, b) => Number(b.category === "locale" || b.category === "auth") - Number(a.category === "locale" || a.category === "auth"));
  report.resources = mapped.slice(0, MAX_RESOURCES);
  report.resourceEntriesDropped = Math.min(1_000_000, (early?.dropped ?? 0) + Math.max(0, mapped.length - MAX_RESOURCES));
  const paint = performance.getEntriesByName("first-contentful-paint")[0];
  if (paint) report.stages.firstContentfulPaintMs = ms(paint.startTime);
  if (early?.failed) startupError("script_failed");
  if (early) early.entries.length = 0;
}

function collectWorker(): Promise<void> {
  const worker = navigator.serviceWorker?.controller;
  if (!worker || typeof MessageChannel === "undefined") return Promise.resolve();
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const done = () => { clearTimeout(timer); channel.port1.close(); channel.port2.close(); resolve(); };
    const timer = setTimeout(done, 150);
    channel.port1.onmessage = (event: MessageEvent) => {
      const data = event.data;
      if (report && data?.type === "STARTUP_DIAGNOSTICS" && /^[A-Za-z0-9._-]{1,80}$/.test(data.workerVersion)) {
        const workerStats: StartupWorker = { version: data.workerVersion };
        for (const key of ["snapshots", "fetches", "cacheHits", "cacheMisses", "duplicateJoins", "peakConcurrency", "failures", "snapshotJoins", "prefetchSkipped"] as const) {
          const value = data.counters?.[key];
          if (Number.isInteger(value) && value >= 0) workerStats[key] = Math.min(1_000_000, value);
        }
        if (Number.isFinite(data.counters?.durationMs)) workerStats.durationMs = ms(data.counters.durationMs);
        if (typeof data.counters?.prefetchActive === "boolean") workerStats.prefetchActive = data.counters.prefetchActive;
        report.worker = workerStats;
      }
      done();
    };
    try { worker.postMessage({ type: "GET_STARTUP_DIAGNOSTICS" }, [channel.port2]); } catch { done(); }
  });
}

function sendReport(): void {
  if (!report || upload !== "pending") return;
  const body = JSON.stringify(report);
  if (body.length > 12 * 1024 || navigator.onLine === false) { upload = "unavailable"; return; }
  upload = "sending";
  // Raw fetch: never enters the mutation outbox, never retries, never delays UI.
  void fetch(ENDPOINT, { method: "POST", credentials: "same-origin", keepalive: true, priority: "low",
    headers: { "content-type": "application/json" }, body
  }).then((response) => { upload = response.status === 202 ? "sent" : "failed"; }).catch(() => { upload = "failed"; });
}

export function finishStartup(outcome: StartupTelemetry["outcome"]): void {
  if (!report || terminal) return;
  collectResources();
  terminal = true;
  clearTimeout(deadline);
  clearTimeout(flushTimer);
  report.outcome = (outcome === "ready" || outcome === "slow") && report.errors.length ? "error" : outcome;
  report.durationMs = report.stages.routeReadyMs ?? ms(performance.now());
  report.visibility = document.visibilityState === "hidden" ? "hidden" : "visible";
  void collectWorker().then(sendReport);
}

function onPageHide(): void {
  if (!terminal) finishStartup("incomplete");
  sendReport();
}

/** Available on error screens and from DevTools; no household data or URL is included. */
export function startupDiagnostics(): string {
  return JSON.stringify({ report: report ?? null, upload }, null, 2);
}
